<template>
    <div class="file-upload-section">
        <el-divider></el-divider>
        <div class="section-header">
            <span v-if="required" class="required-mark">*</span>
            <span class="section-title">{{ title }}</span>
        </div>

        <el-row :gutter="20">
            <el-col :span="8">
                <el-upload
                    ref="upload"
                    drag
                    action="#"
                    :auto-upload="false"
                    :on-change="handleFileChange"
                    :file-list="fileList"
                    :limit="limit"
                    :multiple="multiple"
                    :show-file-list="false"
                    :accept="accept">
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">
                        点击或拖拽文件到此处
                        <div class="upload-tip">
                            <span v-if="tip">{{ tip }}<br></span>
                            <span v-if="accept">支持格式：{{ accept }}<br></span>
                            <span>文件大小不超过 {{ maxFileSize }}MB</span>
                        </div>
                    </div>
                </el-upload>
                <div v-if="templateFile" class="template-links">
                    <el-link type="primary" @click="$emit('download-template', templateFile)">模板下载</el-link>
                    <el-link v-if="showHashTool" type="primary" @click="$emit('download-template', 'Hash.zip')">哈希值生成工具</el-link>
                </div>
            </el-col>

            <el-col :span="16">
                <div class="file-list-container">
                    <p class="list-title">已选择文件：</p>
                    <div v-if="fileList.length > 0" class="file-table-wrapper">
                        <el-table :data="fileList" border>
                            <el-table-column prop="name" label="文件名" min-width="180">
                                <template slot-scope="scope">
                                    <span class="file-name">
                                        <i :class="getFileIcon(scope.row.name)"></i>
                                        {{ scope.row.name }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column label="大小" width="100">
                                <template slot-scope="scope">
                                    {{ scope.row.size ? formatFileSize(scope.row.size) : '-' }}
                                </template>
                            </el-table-column>
                            <el-table-column label="状态" width="120">
                                <template slot-scope="scope">
                                    <el-tag v-if="scope.row.status === 'ready'" type="info" size="small">待上传</el-tag>
                                    <el-tag v-else-if="scope.row.status === 'uploading'" type="warning" size="small">上传中</el-tag>
                                    <el-tag v-else-if="scope.row.status === 'parsed'" type="success" size="small">已完成</el-tag>
                                    <el-tag v-else-if="scope.row.status === 'error'" type="danger" size="small">失败</el-tag>
                                    <el-progress
                                        v-if="scope.row.status === 'uploading' && scope.row.percentage < 100"
                                        :percentage="scope.row.percentage"
                                        :show-text="false"
                                        :stroke-width="6">
                                    </el-progress>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="140">
                                <template slot-scope="scope">
                                    <el-button
                                        type="text"
                                        size="small"
                                        @click="$emit('single-upload', scope.$index)"
                                        :disabled="scope.row.status === 'uploading'">
                                        {{ scope.row.parsed ? '重新上传' : '上传' }}
                                    </el-button>
                                    <el-button
                                        type="text"
                                        size="small"
                                        @click="$emit('remove-file', scope.$index)"
                                        :disabled="scope.row.status === 'uploading'">
                                        删除
                                    </el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                    <div v-else class="empty-list">暂无文件</div>

                    <div class="action-buttons">
                        <el-button @click="$emit('clear-files')" :disabled="fileList.length === 0 || isUploading">清空列表</el-button>
                        <el-button type="primary" @click="$emit('batch-upload')" :disabled="fileList.length === 0 || isUploading">批量上传</el-button>
                    </div>
                </div>
            </el-col>
        </el-row>
    </div>
</template>

<script>
export default {
    name: 'FileUploadSection',
    props: {
        title: { type: String, required: true },
        required: { type: Boolean, default: false },
        fileList: { type: Array, default: () => [] },
        isUploading: { type: Boolean, default: false },
        maxFileSize: { type: Number, default: 100 },
        accept: { type: String, default: '' },
        limit: { type: Number, default: 1 },
        multiple: { type: Boolean, default: false },
        templateFile: { type: String, default: '' },
        showHashTool: { type: Boolean, default: false },
        tip: { type: String, default: '' }
    },
    methods: {
        handleFileChange(file, fileList) {
            this.$emit('file-change', file, fileList);
        },
        getFileIcon(filename) {
            const ext = filename.split('.').pop().toLowerCase();
            const iconMap = {
                'pdf': 'el-icon-document',
                'xlsx': 'el-icon-document',
                'xls': 'el-icon-document',
                'docx': 'el-icon-document',
                'doc': 'el-icon-document',
                'jpg': 'el-icon-picture',
                'jpeg': 'el-icon-picture',
                'png': 'el-icon-picture'
            };
            return iconMap[ext] || 'el-icon-document';
        },
        formatFileSize(size) {
            if (size < 1024) return size + ' B';
            if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
            return (size / 1024 / 1024).toFixed(1) + ' MB';
        }
    }
};
</script>

<style scoped>
.file-upload-section {
    margin-bottom: 30px;
}

.section-header {
    margin-bottom: 20px;
    font-size: 16px;
    font-weight: 500;
}

.required-mark {
    color: #f56c6c;
    margin-right: 4px;
}

.section-title {
    color: #303133;
}

.upload-tip {
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
}

.template-links {
    margin-top: 12px;
    display: flex;
    gap: 16px;
}

.file-list-container {
    min-height: 240px;
}

.list-title {
    margin-bottom: 12px;
    font-weight: 500;
}

.file-table-wrapper {
    max-height: 300px;
    overflow-y: auto;
}

.file-name {
    display: flex;
    align-items: center;
    gap: 8px;
}

.empty-list {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 150px;
    color: #909399;
    background: #fafafa;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
}

.action-buttons {
    margin-top: 12px;
    text-align: center;
}

.action-buttons .el-button {
    margin: 0 8px;
}
</style>
